import {
  getCartItemsProductField,
  getCartItemsSkuField,
  getProductDefaultSkuField,
  getProductNameField,
  getSkuImageField,
  getSkuPriceField,
} from '@packages/systems/commerce/utils/CommerceCollectionUtils';
import {CollectionStateType} from '@packages/systems/core/records/CollectionStateType';
import {
  ExpressionPath,
  Expressions,
  matchExpression,
} from '@packages/systems/wfdl';
import {
  EElement,
  EList,
  ERecord,
  EText,
  Expression,
  ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {identity} from '@packages/utilities/fp/utils';
import {PageRecordType} from '@packages/systems/core/records/PageRecord';
import {isDynBoundElement} from '@packages/systems/dynamo/utils/DynamoElementUtils';
import {
  isDynamoChildrenBinding,
  isDynamoImgBinding,
} from '@packages/systems/dynamo/wfdl';
import {Err, Ok} from '@packages/utilities/fp/result';
import {contains} from '@packages/utilities/platform';
import {
  isCommerceCheckoutFormContainer,
  isCommerceOrderConfirmationContainer,
  isCommerceAddToCartForm,
  isCommerceCartItemImage,
  isCommerceCartOpenLinkCount,
  isCommerceCartOrderValue,
  isCommerceCartProductName,
  isCommerceCartProductPrice,
} from '@packages/utilities/elementTypeUtils';
import {isCheckoutRelatedPage} from './CommerceUtils';

const INNER_HTML_BINDING_PATH: ExpressionPath = [
  {in: 'Record', at: 'dyn'},
  {in: 'Record', at: 'bind'},
  {in: 'Record', at: 'innerHTML'},
];
const SKU_ID_BINDING_PATH: ExpressionPath = [
  {in: 'Record', at: 'dyn'},
  {in: 'Record', at: 'bind'},
  {in: 'Record', at: 'skuId'},
];
const IMAGE_SRC_BINDING_PATH: ExpressionPath = [
  {in: 'Record', at: 'dyn'},
  {in: 'Record', at: 'bind'},
  {in: 'Record', at: 'src'},
];

export const prepareEcommerceElementsOnXscp = (
  root: ExpressionElement,
  collectionState: CollectionStateType
) => {
  const updatedExpression = Expressions.everywhere(
    matchExpression({
      Element: (el) => {
        if (isCommerceAddToCartForm(el)) {
          return bindAddToCartForm(el, collectionState);
        }
        if (isCommerceCartOpenLinkCount(el)) {
          return bindCartCount(el);
        }
        if (isCommerceCartItemImage(el)) {
          return bindCartItemImage(el, collectionState);
        }
        if (isCommerceCartProductName(el)) {
          return bindCartProductName(el, collectionState);
        }
        if (isCommerceCartProductPrice(el)) {
          return bindCartProductPrice(el, collectionState);
        }
        if (isCommerceCartOrderValue(el)) {
          return bindCartOrderValue(el);
        } else {
          return EElement(el);
        }
      },
      _: identity,
    })
  )(EElement(root));
  return Expressions.getElement(updatedExpression) || root;
};

const bindAddToCartForm = (
  element: ExpressionElement,
  collectionState: CollectionStateType
) => {
  const productDefaultSkuField = getProductDefaultSkuField(
    collectionState.collections
  );
  const defaultSkuFieldId = productDefaultSkuField && productDefaultSkuField.id;
  const skuBindingData = EList([
    ERecord({
      id: EText(String(defaultSkuFieldId)),
      slug: EText('default-sku'),
      type: EText('ItemRef'),
    }),
    ERecord({
      id: EText('id'),
      slug: EText('id'),
      type: EText('PlainText'),
    }),
  ]);
  const updatedData = Expressions.sparseSetIn(
    Expressions.getElementData(element),
    SKU_ID_BINDING_PATH,
    skuBindingData
  );
  return EElement(Expressions.setElementData(element, updatedData));
};

const bindCartCount = (element: ExpressionElement) => {
  const userItemsCountBindingData = EList([
    ERecord({
      id: EText(''),
      slug: EText('userItemsCount'),
      type: EText('Number'),
      filter: EText('numberPrecision|0|numberPrecision'),
    }),
  ]);
  const updatedData = Expressions.sparseSetIn(
    Expressions.getElementData(element),
    INNER_HTML_BINDING_PATH,
    userItemsCountBindingData
  );
  return EElement(Expressions.setElementData(element, updatedData));
};

const bindCartItemImage = (
  element: ExpressionElement,
  collectionState: CollectionStateType
) => {
  const skuField = getCartItemsSkuField(collectionState.collections);
  const skuImageField = getSkuImageField(collectionState.collections);
  if (!skuField || !skuImageField) {
    return EElement(element);
  }
  const itemImageBindingData = EList([
    ERecord({
      id: EText(skuField.id),
      slug: EText(skuField.slug),
      type: EText(skuField.type),
    }),
    ERecord({
      id: EText(skuImageField.id),
      slug: EText(skuImageField.slug),
      type: EText(skuImageField.type),
    }),
  ]);
  const updatedData = Expressions.sparseSetIn(
    Expressions.getElementData(element),
    IMAGE_SRC_BINDING_PATH,
    itemImageBindingData
  );
  return EElement(Expressions.setElementData(element, updatedData));
};

const bindCartProductName = (
  element: ExpressionElement,
  collectionState: CollectionStateType
) => {
  const productField = getCartItemsProductField(collectionState.collections);
  const productNameField = getProductNameField(collectionState.collections);
  if (!productField || !productNameField) {
    return EElement(element);
  }
  const productNameBindingData = EList([
    ERecord({
      id: EText(productField.id),
      slug: EText(productField.slug),
      type: EText(productField.type),
    }),
    ERecord({
      id: EText(productNameField.id),
      slug: EText(productNameField.slug),
      type: EText(productNameField.type),
    }),
  ]);
  const updatedData = Expressions.sparseSetIn(
    Expressions.getElementData(element),
    INNER_HTML_BINDING_PATH,
    productNameBindingData
  );
  return EElement(Expressions.setElementData(element, updatedData));
};

const bindCartProductPrice = (
  element: ExpressionElement,
  collectionState: CollectionStateType
) => {
  const skuField = getCartItemsSkuField(collectionState.collections);
  const skuPriceField = getSkuPriceField(collectionState.collections);
  if (!skuField || !skuPriceField) {
    return EElement(element);
  }
  const productPriceBindingData = EList([
    ERecord({
      id: EText(skuField.id),
      slug: EText(skuField.slug),
      type: EText(skuField.type),
    }),
    ERecord({
      id: EText(skuPriceField.id),
      slug: EText(skuPriceField.slug),
      type: EText(skuPriceField.type),
    }),
  ]);
  const updatedData = Expressions.sparseSetIn(
    Expressions.getElementData(element),
    INNER_HTML_BINDING_PATH,
    productPriceBindingData
  );
  return EElement(Expressions.setElementData(element, updatedData));
};

const bindCartOrderValue = (element: ExpressionElement) => {
  const orderValueBindingData = EList([
    ERecord({
      id: EText(''),
      slug: EText('subtotal'),
      type: EText('CommercePrice'),
    }),
  ]);
  const updatedData = Expressions.sparseSetIn(
    Expressions.getElementData(element),
    INNER_HTML_BINDING_PATH,
    orderValueBindingData
  );
  return EElement(Expressions.setElementData(element, updatedData));
};

export const validateEcommerceElementPastePosition = (
  rawParent: ExpressionElement,
  currentComponentRender: Expression,
  rawChild: ExpressionElement,
  page: PageRecordType,
  commerceEnabled: boolean
) => {
  // First make sure not trying to paste a dynamic ecommerce element into site without ecommerce enabled
  if (
    !commerceEnabled &&
    contains(rawChild, isDynamicCommerceElementOrIsDynamicBound)
  ) {
    return Err('Ecommerce has not been enabled on this site.');
  }
  // Need to allow root containers for the Checkout Form and Order Confirmation to be pasted into the body of the right page
  if (
    isCheckoutRelatedPage(page) &&
    contains(
      rawChild,
      isCommerceCheckoutFormContainerOrOrderConfirmationContainer
    )
  ) {
    return Ok(undefined);
  }
  // If element has retained bindings, make sure it is going into the Checkout Form or Order Confirmation.
  // This is because the only copy source that does not have bindings stripped is from these Ecommerce elements.
  if (
    contains(rawChild, isElementDynamicBound) &&
    !Expressions.closestElement(
      currentComponentRender,
      rawParent.id,
      isCommerceCheckoutFormContainerOrOrderConfirmationContainer
    )
  ) {
    return Err(
      'Elements can only be pasted in a Checkout Form or Order Confirmation'
    );
  } else {
    return Ok(undefined);
  }
};

const isDynamicCommerceElementOrIsDynamicBound = (element: ExpressionElement) =>
  isDynamicCommerceElement(element) || isElementDynamicBound(element);

const isCommerceCheckoutFormContainerOrOrderConfirmationContainer = (
  element: ExpressionElement
) =>
  isCommerceCheckoutFormContainer(element) ||
  isCommerceOrderConfirmationContainer(element);

const isDynamicCommerceElement = (element: ExpressionElement): boolean =>
  isCommerceAddToCartForm(element) ||
  isCommerceCartOpenLinkCount(element) ||
  isCommerceCartItemImage(element) ||
  isCommerceCartProductName(element) ||
  isCommerceCartProductPrice(element) ||
  isCommerceCartOrderValue(element);

const isElementDynamicBound = (element: ExpressionElement) => {
  return (
    isDynBoundElement(element) ||
    isDynamoChildrenBinding(element.data) ||
    isDynamoImgBinding(element.data)
  );
};
